import { useEffect, useMemo, useState } from "react";
import { RotateCcw, Trash2, UserCog, X } from "lucide-react";
import Button from "../ui/Button";
import Select from "../ui/Select";
import StatusBadge from "./StatusBadge";
import { ROLES } from "../../config/roles";
import usePermissions from "../../hooks/usePermissions";
import { getPermissionLabel } from "../../config/permissions";
import {
  getRoleApiSlug,
  getRoleDisplayLabel,
  getRolePermissions,
  listAssignableRoles,
  listRoles,
  resolveRoleKeyFromSlug,
  subscribeRolePermissions,
} from "../../data/opsRolePermissionsStore";

function PermissionChips({ permissions }) {
  const keys = Array.isArray(permissions) ? permissions : [];
  if (!keys.length) {
    return <p className="text-[11px] text-primary/40">No permissions on this role.</p>;
  }
  return (
    <div className="flex max-h-40 flex-wrap gap-1.5 overflow-y-auto rounded-xl border border-primary/[0.07] bg-primary/[0.02] p-2.5">
      {keys.map((key) => (
        <span
          key={key}
          title={key}
          className="rounded-md bg-white px-2 py-1 text-[10px] font-semibold text-primary/70 ring-1 ring-primary/[0.08]"
        >
          {getPermissionLabel(key)}
        </span>
      ))}
    </div>
  );
}

export default function ManageOpsUserModal({
  user,
  onClose,
  onSave,
  onDelete,
  onRestore,
}) {
  const { role: actorRole } = usePermissions();
  const [roles, setRoles] = useState(listRoles);
  const [firstName, setFirstName] = useState(user.first_name || "");
  const [lastName, setLastName] = useState(user.last_name || "");
  const [email, setEmail] = useState(user.email || "");
  const [phone, setPhone] = useState(String(user.phone_number || "").replace(/^\+/, ""));
  const [role, setRole] = useState(
    resolveRoleKeyFromSlug(user.role || user.role_slug) || user.role || ""
  );
  const [status, setStatus] = useState(
    user.deleted_at
      ? "deleted"
      : user.status === true || user.status === "active"
        ? "active"
        : user.status === "invited"
          ? "invited"
          : "inactive"
  );
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    return subscribeRolePermissions(() => setRoles(listRoles()));
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !saving && !deleting && !restoring) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, saving, deleting, restoring]);

  const roleOptions = useMemo(() => {
    const assignable = listAssignableRoles({ actorRole });
    const pool = [...new Set([...assignable, user.role].filter(Boolean))];

    return pool.map((r) => ({
      value: r,
      label: getRoleDisplayLabel(r),
      description:
        r === ROLES.SUPER_ADMIN
          ? "Full platform access — multiple super admins allowed"
          : getRoleApiSlug(r)
            ? `${getRolePermissions(r).length} permissions`
            : "Role UUID missing — reload roles",
      disabled: !getRoleApiSlug(r),
    }));
  }, [roles, user.role, actorRole]);

  const statusOptions = [
    { value: "active", label: "Active", description: "Can sign in to the ops portal" },
    { value: "inactive", label: "Inactive", description: "Account disabled" },
  ];

  if (!user) return null;

  const isDeleted = Boolean(user.deleted_at) || status === "deleted";
  const busy = saving || deleting || restoring;

  const canSave =
    firstName.trim() &&
    email.trim() &&
    phone.trim() &&
    role &&
    Boolean(getRoleApiSlug(role)) &&
    !busy &&
    !isDeleted;

  const handleSave = async () => {
    if (!canSave) return;
    const roleSlug = getRoleApiSlug(role);
    if (!roleSlug) {
      return;
    }
    setSaving(true);
    try {
      await onSave({
        admin_slug: user.admin_slug || user.id,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        phone_number: phone.trim(),
        role_slug: roleSlug,
        role,
        status: status === "inactive" ? "inactive" : "active",
      });
      onClose();
    } catch {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete || busy) return;
    setDeleting(true);
    try {
      await onDelete(user);
      onClose();
    } catch {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleRestore = async () => {
    if (!onRestore || busy) return;
    setRestoring(true);
    try {
      await onRestore(user);
      onClose();
    } catch {
      setRestoring(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-primary/40 p-4 backdrop-blur-sm sm:items-center"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        role="dialog"
        aria-labelledby="manage-ops-user-title"
        className="flex max-h-[min(94vh,900px)] w-full max-w-2xl flex-col overflow-visible rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-primary/[0.06] px-5 py-4 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/5 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-primary/55">
                <UserCog className="h-3 w-3" />
                Manage access
              </span>
              <h2 id="manage-ops-user-title" className="mt-2 text-lg font-bold text-primary">
                {user.name}
              </h2>
              <p className="mt-1 text-sm text-primary/45">{user.email}</p>
              <div className="mt-2">
                <StatusBadge
                  status={
                    isDeleted
                      ? "closed"
                      : status === "active"
                        ? "active"
                        : status === "inactive"
                          ? "closed"
                          : "pending"
                  }
                  label={
                    isDeleted
                      ? "Deleted"
                      : status === "active"
                        ? "Active"
                        : status === "inactive"
                          ? "Inactive"
                          : "Invited"
                  }
                />
              </div>
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={onClose}
              className="rounded-lg p-2 text-primary/40 hover:bg-primary/5 disabled:opacity-40"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="space-y-4 overflow-y-auto px-5 py-5 sm:px-6" style={{ overflowX: "visible" }}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-primary/50">First name</label>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={busy || isDeleted}
                className="w-full rounded-lg border border-primary/10 px-3 py-2.5 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:opacity-60"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-primary/50">
                Last name <span className="font-medium text-primary/35">(optional)</span>
              </label>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={busy || isDeleted}
                className="w-full rounded-lg border border-primary/10 px-3 py-2.5 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:opacity-60"
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-primary/50">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy || isDeleted}
                className="w-full rounded-lg border border-primary/10 px-3 py-2.5 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:opacity-60"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-primary/50">Phone number</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={busy || isDeleted}
                className="w-full rounded-lg border border-primary/10 px-3 py-2.5 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:opacity-60"
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Role"
              value={role}
              onChange={setRole}
              options={roleOptions}
              disabled={busy || isDeleted}
            />
            {!isDeleted ? (
              <Select
                label="Status"
                value={status === "invited" ? "active" : status}
                onChange={setStatus}
                options={statusOptions}
                disabled={busy}
              />
            ) : null}
          </div>

          <div>
            <p className="mb-1.5 text-xs font-semibold text-primary/50">
              Permissions ({getRolePermissions(role).length})
            </p>
            <PermissionChips permissions={getRolePermissions(role)} />
          </div>
        </div>

        <div className="flex flex-wrap justify-between gap-2 border-t border-primary/[0.06] px-5 py-4 sm:px-6">
          <div className="flex flex-wrap gap-2">
            {isDeleted && onRestore ? (
              <Button variant="ghost" size="sm" disabled={busy} onClick={handleRestore}>
                <RotateCcw className="h-3.5 w-3.5" />
                {restoring ? "Restoring…" : "Restore"}
              </Button>
            ) : null}
            {!isDeleted && onDelete ? (
              confirmDelete ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:bg-red-50"
                  disabled={busy}
                  onClick={handleDelete}
                >
                  {deleting ? "Deleting…" : "Confirm delete"}
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:bg-red-50"
                  disabled={busy}
                  onClick={() => setConfirmDelete(true)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </Button>
              )
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            {!isDeleted ? (
              <Button size="sm" onClick={handleSave} disabled={!canSave}>
                {saving ? "Saving…" : "Save changes"}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
